"use client";
import { createContext as e, useContext as t, useSyncExternalStore as n } from "react";
import { xoroshiro128plus as r } from "pure-rand/generator/xoroshiro128plus";
//#region src/core/currency.ts
var i = {
	name: "credit",
	namePlural: "credits",
	symbol: "$",
	multiplier: 100,
	iconSvg: ""
};
function a(e) {
	switch (e) {
		case 1: return 0;
		case 10: return 1;
		case 100: return 2;
		case 1e3: return 3;
	}
}
var o = [
	0,
	1,
	2,
	3
].map((e) => new Intl.NumberFormat("en-US", {
	minimumFractionDigits: e,
	maximumFractionDigits: e
}));
function s(e, t, n = {}) {
	if (!Number.isFinite(e)) throw RangeError(`formatAmount: amount is not a finite number of cents: ${e}`);
	let r = a(t.multiplier), i = Math.abs(e) / t.multiplier, s = e < 0 ? "-" : "", c = n.compact && i >= 1e3 ? `${(i / 1e3).toFixed(1)}k` : o[r].format(i);
	return `${s}${t.symbol}${c}`;
}
function c(e, t) {
	return e === t.multiplier ? t.name : t.namePlural;
}
function l(e, t) {
	let n = s(e, t);
	return t.symbol === "" ? `${n} ${c(e, t)}` : n;
}
//#endregion
//#region src/i18n/strings.ts
var u = {
	open: "Open",
	openAgain: "Open again",
	skip: "Skip",
	muteSounds: "Mute sounds",
	quantity: "Quantity",
	quickReveal: "Quick reveal",
	claim: "Claim items",
	claiming: "Claiming…",
	sellBack: "Sell back",
	sellingBack: "Selling back…",
	errorSellBackFailed: "Selling back failed. Please try again.",
	withdrawItem: "Withdraw",
	withdrawRequested: "Withdrawal started",
	limitedItemNotice: "Limited item. If you cannot withdraw it, you keep it and can sell it for its value.",
	substituteBadge: "Cannot be shipped",
	autoClaimLabel: "Auto-claim future opens",
	errorClaimFailed: "Claiming failed. Please try again.",
	totalValue: "Total value",
	loading: "Loading…",
	demoBadge: "Demo",
	errorGeneric: "Something went wrong. Please try again.",
	errorInsufficientBalance: "Not enough balance to open this box.",
	errorAccountLocked: "This account is locked. Contact support.",
	provablyFair: "Provably fair",
	serverSeedHash: "Server seed hash",
	clientSeed: "Client seed",
	salt: "Salt",
	seedDate: "Seed date",
	nonce: "Nonce",
	result: "Result",
	noOpensYet: "Open the box to record provably-fair rolls here.",
	battleStartsIn: "Battle starts in",
	battleGoodLuck: "Good luck!",
	battleGo: "GO",
	battleRound: "Round"
};
function d(e) {
	return e ? {
		...u,
		...e
	} : u;
}
//#endregion
//#region ../api-contract/src/storefrontThemeVars.ts
var f = /^(#[0-9a-fA-F]{3,8}|(?:oklch|rgba?|hsla?)\([0-9a-zA-Z.%,/\s+-]+\)|[a-zA-Z]{3,20})$/, p = /^(\d{1,3}(?:\.\d+)?)(px|rem|em|%)?$/, m = /^[\w\s,'"-]{1,100}$/;
function h(e) {
	let t = e?.trim() ?? "";
	return t && f.test(t) ? t : null;
}
function g(e) {
	let t = p.exec(e?.trim() ?? "");
	return t ? t[2] ? t[0] : `${t[1]}px` : null;
}
function _(e) {
	let t = e?.trim() ?? "";
	return t && m.test(t) ? t : null;
}
//#endregion
//#region ../api-contract/src/svgIcon.ts
function v(e) {
	return `data:image/svg+xml,${encodeURIComponent(e)}`;
}
//#endregion
//#region src/theme/rarity.ts
var y = {
	0: "179,179,179",
	1: "34,197,94",
	2: "59,130,246",
	3: "168,85,247",
	4: "249,115,22",
	5: "249,115,22"
}, b = {
	0: "#A9A9CA",
	1: "#22C55E",
	2: "#3B82F6",
	3: "#A855F7",
	4: "#FFC700",
	5: "#FFC700"
}, x = "#FFFFFF";
function S(e, t, n, r) {
	return `drop-shadow(0 0 ${n} rgba(${e[t] || "255,255,255"},${r}))`;
}
function C(e, t) {
	switch (t) {
		case 0: return S(e, t, "15px", "0.3");
		case 1: return S(e, t, "15px", "0.4");
		case 2: return S(e, t, "15px", "0.7");
		case 3:
		case 4:
		case 5: return S(e, t, "15px", "1");
		default: return S(e, t, "15px", "1");
	}
}
function w(e, t) {
	return t == null ? x : e[t] ?? "#FFFFFF";
}
//#endregion
//#region src/theme/theme.ts
var T = {
	background: "#0e0f1a",
	surface: "#131420",
	border: "#2a2b3d",
	text: "#ffffff",
	textMuted: "#a9a9ca",
	accent: "#8b5cf6",
	accentForeground: "#ffffff"
};
function E(e) {
	let t = e.trim();
	if (/^\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}$/.test(t)) return t.replace(/\s+/g, "");
	let n = t.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i)?.[1];
	if (n) {
		let e = n.length === 3 ? n.replace(/./g, (e) => e + e) : n;
		return `${Number.parseInt(e.slice(0, 2), 16)},${Number.parseInt(e.slice(2, 4), 16)},${Number.parseInt(e.slice(4, 6), 16)}`;
	}
	let r = t.match(/^rgba?\(\s*(\d{1,3})\s*[, ]\s*(\d{1,3})\s*[, ]\s*(\d{1,3})/i);
	return r ? `${r[1]},${r[2]},${r[3]}` : null;
}
function D(e) {
	let [t = 0, n = 0, r = 0] = e.split(",").map((e) => Number.parseInt(e, 10)), i = (e) => Math.max(0, Math.min(255, e)).toString(16).padStart(2, "0");
	return `#${i(t)}${i(n)}${i(r)}`;
}
var O = [
	0,
	1,
	2,
	3,
	4,
	5
], k = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i, A = /^[a-z][a-zA-Z0-9]{0,31}$/;
function j(e) {
	let t = {
		...T,
		...e?.colors
	}, n = { ...y }, r = { ...b };
	for (let t of O) {
		let i = e?.rarity?.[t];
		if (!i) continue;
		let a = E(i);
		a && (n[t] = a, r[t] = D(a));
	}
	let i = {};
	for (let [t, n] of Object.entries(e?.kind ?? {})) A.test(t) && typeof n == "string" && k.test(n) && (i[t] = n);
	return {
		cssVars: {
			"--los-bg": t.background,
			"--los-surface": t.surface,
			"--los-border": t.border,
			"--los-text": t.text,
			"--los-text-muted": t.textMuted,
			"--los-accent": t.accent,
			"--los-accent-foreground": t.accentForeground,
			"--los-radius": e?.radius ?? "16px",
			"--los-font": e?.fontFamily ?? "inherit",
			...Object.fromEntries(Object.entries(i).map(([e, t]) => [`--los-kind-${e}`, t]))
		},
		colors: t,
		rarityRgb: n,
		railHex: r,
		kind: i
	};
}
function M(e, t) {
	if (!t) return e;
	let n = { ...e?.colors };
	for (let [e, r] of Object.entries(t.colors ?? {})) {
		let t = typeof r == "string" ? h(r) : null;
		t && (n[e] = t);
	}
	let r = { ...e?.rarity };
	for (let e of O) {
		let n = t.rarity?.[String(e)], i = typeof n == "string" ? h(n) : null;
		i && (r[e] = i);
	}
	let i = g(t.radius) ?? e?.radius, a = _(t.fontFamily) ?? e?.fontFamily, o = {
		...e?.kind,
		...t.kind
	};
	return {
		...Object.keys(n).length ? { colors: n } : {},
		...Object.keys(r).length ? { rarity: r } : {},
		...i ? { radius: i } : {},
		...a ? { fontFamily: a } : {},
		...Object.keys(o).length ? { kind: o } : {}
	};
}
function N(e, t) {
	return Object.fromEntries(Object.entries(t).map(([t, n]) => [t, e.kind[t] ?? ("role" in n ? e.colors[n.role] : n.color)]));
}
//#endregion
//#region src/react/LootBoxContext.tsx
var P = () => {}, F = {
	strings: u,
	theme: j(),
	sounds: {
		playTickSound: P,
		playRaritySound: P,
		unlock: P,
		enabled: !1,
		configured: !1,
		muted: !1,
		gain: 1,
		subscribe: () => P
	},
	currency: i
}, I = e(F);
function L() {
	return t(I);
}
//#endregion
//#region src/core/dropTable.ts
var R = 1e6;
function z(e, t) {
	let n = e / t;
	return n === Infinity ? 0 : n > .7 && n < 1 ? 1 : n >= 1 && n < 1.5 ? 2 : n >= 1.5 && n < 6.5 ? 3 : n >= 6.5 ? 4 : 0;
}
function B(e) {
	let t = (e || 0) / R * 100;
	return Number(t.toFixed(4));
}
function V(e, t) {
	let n = 0;
	for (let t = 0; t < e.length; t++) n += e[t]?.weight ?? 0;
	if (n !== 1e6) throw Error(`[loot-os] drop-table weights must sum to ${R}, got ${n}`);
	let r = t;
	for (let t = 0; t < e.length; t++) if (r -= e[t]?.weight ?? 0, r < 0) return t;
}
//#endregion
//#region src/battle/seed.ts
function H(e) {
	let t = 2166136261;
	for (let n = 0; n < e.length; n++) t ^= e.charCodeAt(n), t = Math.imul(t, 16777619);
	return t | 0;
}
function U(e, t = 0) {
	return r(H(e) + t);
}
//#endregion
//#region src/sounds/useOpenerSoundLevel.ts
function W(e) {
	return {
		muted: n(e.subscribe, () => e.muted, () => e.muted),
		gain: n(e.subscribe, () => e.gain, () => e.gain)
	};
}
//#endregion
export { c as C, a as S, l as T, w as _, B as a, d as b, I as c, N as d, j as f, C as g, S as h, R as i, L as l, y as m, U as n, z as o, b as p, H as r, V as s, W as t, M as u, v, s as w, i as x, u as y };

//# sourceMappingURL=useOpenerSoundLevel-BgfG5O7E.js.map